import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { C, Input, Badge, Skeleton } from "@coremonit/design-system";
import {
  ChevronRight, ChevronDown, Star, Search, Server, Boxes, ContainerIcon,
  FolderKanban, Cloud, Globe, Layers, Building2, History, Pin,
} from "lucide-react";
import { useInfrastructureTree } from "../api/hooks";
import { useExplorer } from "../context/ExplorerContext";
import { HealthDot } from "../components/HealthScoreRing";
import type { InfrastructureNode, InfrastructureKind } from "@coremonit/types";

const KIND_ICON: Partial<Record<InfrastructureKind, typeof Server>> = {
  organization: Building2, client: FolderKanban, environment: Globe,
  cloud_provider: Cloud, cluster: Server, namespace: Layers,
  project: FolderKanban, microservice: Boxes, deployment: Layers,
  pod: ContainerIcon, container: ContainerIcon,
};

const PRESETS = [
  { id: "client_based", label: "Client" },
  { id: "project_based", label: "Project" },
  { id: "environment_based", label: "Environment" },
  { id: "region_based", label: "Region" },
  { id: "team_based", label: "Team" },
];

interface Props { collapsed?: boolean; embedded?: boolean }

export function InfrastructureExplorer({ collapsed, embedded }: Props) {
  const { preset, setPreset, favorites, toggleFavorite, recent, pushRecent } = useExplorer();
  const tree = useInfrastructureTree(preset);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["org_root"]));
  const [tab, setTab] = useState<"tree" | "recent" | "favorites">("tree");
  const nav = useNavigate();

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const flatFiltered = useMemo(() => {
    if (!tree.data?.root || !q.trim()) return null;
    const needle = q.toLowerCase();
    const out: InfrastructureNode[] = [];
    const walk = (n: InfrastructureNode) => {
      if (n.name.toLowerCase().includes(needle) || n.label.toLowerCase().includes(needle)) out.push(n);
      n.children.forEach(walk);
    };
    walk(tree.data.root);
    return out.slice(0, 40);
  }, [tree.data, q]);

  const openNode = (n: InfrastructureNode) => {
    pushRecent({ id: n.id, name: n.name, path: n.path });
    nav(n.path);
  };

  if (collapsed) {
    return (
      <div style={{ padding: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <span title="Infrastructure Explorer"><Server size={16} color={C.blue} /></span>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: embedded ? "auto" : "100%",
      minHeight: 0,
      maxHeight: embedded ? 360 : undefined,
    }}>
      {!embedded && (
        <div style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Infrastructure Explorer</div>
          <Input value={q} onChange={setQ} prefix={<Search size={12} />} placeholder="Filter tree…" />
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            style={{ width: "100%", marginTop: 6, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textSub, fontSize: 10, padding: "5px 8px" }}
          >
            {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label} hierarchy</option>)}
          </select>
        </div>
      )}
      {embedded && (
        <div style={{ padding: "6px 4px 8px", borderBottom: `1px solid ${C.border}` }}>
          <Input value={q} onChange={setQ} prefix={<Search size={12} />} placeholder="Filter tree…" />
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            style={{ width: "100%", marginTop: 6, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textSub, fontSize: 10, padding: "5px 8px" }}
          >
            {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label} hierarchy</option>)}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: 2, padding: "6px 8px", borderBottom: `1px solid ${C.border}` }}>
        {(["tree", "recent", "favorites"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1, padding: "4px 0", fontSize: 9, fontWeight: 700, textTransform: "uppercase",
              background: tab === t ? C.blueDim : "transparent",
              border: `1px solid ${tab === t ? C.borderBright : "transparent"}`,
              borderRadius: 6, color: tab === t ? C.blue : C.textMuted, cursor: "pointer",
            }}
          >
            {t === "tree" ? "Tree" : t === "recent" ? "Recent" : "★"}
          </button>
        ))}
      </div>

      <div style={{ flex: embedded ? undefined : 1, overflowY: "auto", padding: "6px 4px", maxHeight: embedded ? 260 : undefined }}>
        {tree.isLoading && <Skeleton height={200} />}
        {tab === "tree" && !tree.isLoading && tree.data && (
          q.trim() && flatFiltered ? (
            flatFiltered.map((n) => <NodeRow key={n.id} node={n} depth={0} expanded={expanded} onToggle={toggle} onOpen={openNode} favorites={favorites} onFav={toggleFavorite} flat />)
          ) : (
            <NodeRow node={tree.data.root} depth={0} expanded={expanded} onToggle={toggle} onOpen={openNode} favorites={favorites} onFav={toggleFavorite} />
          )
        )}
        {tab === "recent" && recent.map((r) => (
          <button key={r.id} onClick={() => nav(r.path)} style={{ width: "100%", textAlign: "left", padding: "6px 8px", background: "transparent", border: "none", color: C.textSub, fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <History size={10} color={C.textMuted} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
          </button>
        ))}
        {tab === "favorites" && tree.data && (
          <FavoriteNodes root={tree.data.root} favorites={favorites} onOpen={openNode} />
        )}
      </div>
    </div>
  );
}

function FavoriteNodes({ root, favorites, onOpen }: { root: InfrastructureNode; favorites: Set<string>; onOpen: (n: InfrastructureNode) => void }) {
  const nodes: InfrastructureNode[] = [];
  const walk = (n: InfrastructureNode) => {
    if (favorites.has(n.id)) nodes.push(n);
    n.children.forEach(walk);
  };
  walk(root);
  if (!nodes.length) return <div style={{ padding: 12, color: C.textMuted, fontSize: 10 }}>Star items in the tree to pin them here.</div>;
  return <>{nodes.map((n) => (
    <button key={n.id} onClick={() => onOpen(n)} style={{ width: "100%", textAlign: "left", padding: "6px 8px", background: "transparent", border: "none", color: C.text, fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
      <Star size={10} color={C.amber} fill={C.amber} /> {n.name}
    </button>
  ))}</>;
}

function NodeRow({
  node, depth, expanded, onToggle, onOpen, favorites, onFav, flat,
}: {
  node: InfrastructureNode; depth: number;
  expanded: Set<string>; onToggle: (id: string) => void;
  onOpen: (n: InfrastructureNode) => void;
  favorites: Set<string>; onFav: (id: string) => void;
  flat?: boolean;
}) {
  const hasChildren = node.children.length > 0 && !flat;
  const isOpen = expanded.has(node.id);
  const Icon = KIND_ICON[node.kind] ?? Boxes;
  const isFav = favorites.has(node.id);

  return (
    <div>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 4,
          padding: "3px 4px 3px 0", paddingLeft: depth * 12 + 4,
          borderRadius: 6, cursor: "pointer",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = C.surfaceHigh; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id); }}
          style={{ width: 14, background: "transparent", border: "none", color: C.textMuted, cursor: hasChildren ? "pointer" : "default", padding: 0, display: "flex" }}
        >
          {hasChildren ? (isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : <span style={{ width: 12 }} />}
        </button>
        <HealthDot status={node.health.status} />
        <button
          onClick={() => onOpen(node)}
          style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 5, background: "transparent", border: "none", color: C.textSub, fontSize: 10, fontWeight: 500, cursor: "pointer", textAlign: "left", padding: "2px 0" }}
        >
          <Icon size={11} color={C.blue} style={{ flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
        </button>
        {node.metrics.alertCount > 0 && <Badge color="red">{node.metrics.alertCount}</Badge>}
        <button
          onClick={(e) => { e.stopPropagation(); onFav(node.id); }}
          style={{ background: "transparent", border: "none", cursor: "pointer", padding: 2, opacity: isFav ? 1 : 0.3 }}
          title={isFav ? "Unpin" : "Pin"}
        >
          {isFav ? <Star size={10} color={C.amber} fill={C.amber} /> : <Pin size={10} color={C.textMuted} />}
        </button>
      </div>
      {hasChildren && isOpen && node.children.map((c) => (
        <NodeRow key={c.id} node={c} depth={depth + 1} expanded={expanded} onToggle={onToggle} onOpen={onOpen} favorites={favorites} onFav={onFav} />
      ))}
    </div>
  );
}
